import { useState } from "react";
import { formatCurrencyPrecise as formatCurrency } from "@/lib/format-utils";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  TrendingUp,
  Calendar,
  AlertCircle,
  Clock,
  User,
} from "lucide-react";
import { format } from "date-fns";
import type { Task } from "@shared/schema";

type TimeFilter = "month" | "ytd" | "all";

interface ClaimsMetrics {
  totalClaims: number;
  assignedClaims: number;
  pendingLeads: number;
  convertedLeads: number;
  totalClaimOffers: number;
  totalOffersOutstanding: number;
  companyFees: number;
  balanceDue: number;
}

interface ClaimsByType {
  type: string;
  count: number;
  totalNetOffers: number;
}

interface ClaimsByStatus {
  status: string;
  count: number;
  totalNetOffers: number;
  companyFees: number;
  balanceDue: number;
}

interface AdjusterDashboardContentProps {
  gmailCard?: React.ReactNode;
}

export default function AdjusterDashboardContent({ gmailCard }: AdjusterDashboardContentProps = {}) {
  const [lossTypeFilter, setLossTypeFilter] = useState<TimeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<TimeFilter>("all");

  // Fetch adjuster metrics
  const { data: metrics, isLoading: metricsLoading } = useQuery<ClaimsMetrics>({
    queryKey: ['/api/adjuster/metrics']
  });

  // Fetch claims by loss type
  const { data: claimsByType } = useQuery<ClaimsByType[]>({
    queryKey: [`/api/adjuster/claims-by-type?filter=${lossTypeFilter}`]
  });

  // Fetch claims by status
  const { data: claimsByStatus } = useQuery<ClaimsByStatus[]>({
    queryKey: [`/api/adjuster/claims-by-status?filter=${statusFilter}`]
  });

  // Fetch assigned tasks
  const { data: assignedTasks } = useQuery<Task[]>({
    queryKey: ['/api/adjuster/tasks']
  });

  // Fetch upcoming meetings/agenda
  const { data: meetings } = useQuery<any[]>({
    queryKey: ['/api/adjuster/meetings']
  });



  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'destructive';
      case 'normal':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              My Assigned Claims
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Link 
                  href="/claims"
                  className="hover:underline cursor-pointer"
                >
                  <p 
                    className="text-2xl font-bold transition-colors hover:text-primary" 
                    data-testid="text-assigned-claims"
                  >
                    {metrics?.assignedClaims || 0}
                  </p>
                </Link>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Link 
                  href="/leads"
                  className="hover:underline cursor-pointer"
                >
                  <p 
                    className="text-2xl font-bold transition-colors hover:text-primary" 
                    data-testid="text-pending-leads"
                  >
                    {metrics?.pendingLeads || 0}
                  </p>
                </Link>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {metrics?.convertedLeads || 0} Converted
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Financial Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Claim Offers</p>
              <Link 
                href="/reports"
                className="hover:underline cursor-pointer"
              >
                <p 
                  className="text-xl font-bold transition-colors hover:text-primary" 
                  data-testid="text-total-offers"
                >
                  {formatCurrency(metrics?.totalClaimOffers || 0)}
                </p>
              </Link>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Offers Outstanding</p>
              <Link 
                href="/reports"
                className="hover:underline cursor-pointer"
              >
                <p 
                  className="text-xl font-bold text-orange-600 dark:text-orange-400 transition-colors hover:text-primary dark:hover:text-primary"
                  data-testid="text-outstanding"
                >
                  {formatCurrency(metrics?.totalOffersOutstanding || 0)}
                </p>
              </Link>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Company Fees</p>
              <Link 
                href="/commissions"
                className="hover:underline cursor-pointer"
              >
                <p 
                  className="text-xl font-bold transition-colors hover:text-primary" 
                  data-testid="text-fees"
                >
                  {formatCurrency(metrics?.companyFees || 0)}
                </p>
              </Link>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Balance Due</p>
              <Link 
                href="/commissions"
                className="hover:underline cursor-pointer"
              >
                <p 
                  className="text-xl font-bold text-orange-600 dark:text-orange-400 transition-colors hover:text-primary dark:hover:text-primary"
                  data-testid="text-balance"
                >
                  {formatCurrency(metrics?.balanceDue || 0)}
                </p>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Claims by Loss Type */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Claims by Loss Type</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant={lossTypeFilter === "month" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setLossTypeFilter("month")}
                  data-testid="button-month-loss"
                >
                  Month
                </Button>
                <Button
                  variant={lossTypeFilter === "ytd" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setLossTypeFilter("ytd")}
                  data-testid="button-ytd-loss"
                >
                  YTD
                </Button>
                <Button
                  variant={lossTypeFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setLossTypeFilter("all")}
                  data-testid="button-all-loss"
                >
                  All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {claimsByType?.map((item) => (
                <Link 
                  key={item.type}
                  href="/claims"
                  className="block hover:bg-accent/50 transition-colors rounded-lg"
                >
                  <div className="border-l-4 border-l-primary pl-4 py-2">
                    <p className="font-medium" data-testid={`text-loss-type-${item.type}`}>
                      {item.type}
                    </p>
                    <p className="text-2xl font-bold hover:text-primary transition-colors" data-testid={`text-loss-count-${item.type}`}>
                      {item.count}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Claims</p>
                    <p className="text-lg font-semibold hover:text-primary transition-colors" data-testid={`text-loss-amount-${item.type}`}>
                      {formatCurrency(item.totalNetOffers)}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Net Offers</p>
                  </div>
                </Link>
              ))}
              {!claimsByType?.length && (
                <p className="text-muted-foreground text-center py-4">
                  No claims data available
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Claims by Status */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Claims by Status</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant={statusFilter === "month" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("month")}
                  data-testid="button-month-status"
                >
                  Month
                </Button>
                <Button
                  variant={statusFilter === "ytd" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("ytd")}
                  data-testid="button-ytd-status"
                >
                  YTD
                </Button>
                <Button
                  variant={statusFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("all")}
                  data-testid="button-all-status"
                >
                  All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {claimsByStatus?.map((item) => (
                <Link 
                  key={item.status}
                  href="/claims"
                  className="block"
                >
                  <div className="bg-secondary/10 rounded-lg p-4 hover:bg-secondary/20 transition-colors cursor-pointer">
                    <p className="font-medium text-green-700 dark:text-green-400 mb-2" data-testid={`text-status-${item.status}`}>
                      {item.status}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Claims: </span>
                        <span className="font-semibold hover:text-primary transition-colors">{item.count}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Net Offers: </span>
                        <span className="font-semibold hover:text-primary transition-colors">{formatCurrency(item.totalNetOffers)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Company Fees: </span>
                        <span className="font-semibold hover:text-primary transition-colors">{formatCurrency(item.companyFees)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Balance Due: </span>
                        <span className="font-semibold hover:text-primary transition-colors">{formatCurrency(item.balanceDue)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
              {!claimsByStatus?.length && (
                <p className="text-muted-foreground text-center py-4">
                  No status data available
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agenda and Tasks */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Agenda/Meetings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Agenda
            </CardTitle>
          </CardHeader>
          <CardContent>
            {meetings?.length ? (
              <div className="space-y-3">
                {meetings.map((meeting: any) => (
                  <div key={meeting.id} className="border-l-4 border-l-primary pl-4">
                    <p className="font-medium">{meeting.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(meeting.date), 'PPp')}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-2 opacity-50" />
                <p className="text-muted-foreground">No upcoming meetings for you.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assigned Tasks */}
        <Card>
          <CardHeader>
            <CardTitle>Assigned Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {assignedTasks?.map((task: any) => (
                <div 
                  key={task.id} 
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  data-testid={`task-${task.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id={`task-${task.id}`}
                      data-testid={`checkbox-task-${task.id}`}
                    />
                    <div className="flex-1">
                      <label 
                        htmlFor={`task-${task.id}`} 
                        className="font-medium cursor-pointer"
                      >
                        {task.title}
                      </label>
                      <ul className="text-sm text-muted-foreground mt-2 space-y-1.5 ml-1">
                        <li className="flex items-start gap-2">
                          <span className="text-muted-foreground mt-1.5">•</span>
                          <span>{task.description}</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-muted-foreground">•</span>
                          <span>Priority:</span>
                          <Badge variant={getPriorityColor(task.priority)}>
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {task.priority}
                          </Badge>
                        </li>
                        {task.assignedTo && (
                          <li className="flex items-center gap-2">
                            <span className="text-muted-foreground">•</span>
                            <User className="h-4 w-4" />
                            <span>Assigned: {task.assignedTo}</span>
                          </li>
                        )}
                        <li className="flex items-center gap-2">
                          <span className="text-muted-foreground">•</span>
                          <Clock className="h-4 w-4" />
                          <span>Due: {task.dueDate ? format(new Date(task.dueDate), 'MMM dd, yyyy') : 'No date'}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
              {!assignedTasks?.length && (
                <p className="text-muted-foreground text-center py-4">
                  No tasks assigned
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      {gmailCard}
    </div>
  );
}
